import {
  IndividualQuota,
  NestedQuota,
} from '../../../shared/interfaces/quota.interface';
import { IndividualQuotas } from './individual-quotas';

type Change = 'count-change' | 'percentage-change';
export class IndividualQuotaCountDistributor {
  private get _individualQuotas(): IndividualQuota[] {
    return this._groupQuota.individual_quotas || [];
  }

  constructor(
    private readonly _groupQuota: NestedQuota,
    private readonly _surveyAllocation: number,
    private readonly _changeType: Change
  ) {}

  public static forCountChange(
    groupedQuota: NestedQuota,
    surveyAllocation: number
  ) {
    return new IndividualQuotaCountDistributor(
      groupedQuota,
      surveyAllocation,
      'count-change'
    );
  }

  public static forPercentageChange(
    groupedQuota: NestedQuota,
    surveyAllocation: number
  ) {
    return new IndividualQuotaCountDistributor(
      groupedQuota,
      surveyAllocation,
      'percentage-change'
    );
  }

  public distribute(
    groupCount: number,
    groupPercentage: number
  ): IndividualQuota[] {
    return this._individualQuotas.map((q) =>
      this._updateEach(q, groupCount, groupPercentage)
    );
  }

  private _currentTotalAllocation(): number {
    return new IndividualQuotas(this._individualQuotas).allocation();
  }

  private _currentTotalPercentage(): number {
    return new IndividualQuotas(this._individualQuotas).percentage();
  }

  private _updateEach(
    quota: IndividualQuota,
    groupCount: number,
    groupPercentage: number
  ): IndividualQuota {
    return {
      ...quota,
      allocation: this._updateAllocation(quota, groupCount, groupPercentage),
    };
  }

  private _updateAllocation(
    quota: IndividualQuota,
    groupCount: number,
    groupPercentage: number
  ): IndividualQuota['allocation'] {
    if (this._changeType === 'count-change') {
      const updatedPercentage = this._updatedPercentage(quota, groupPercentage);
      return {
        ...quota.allocation,
        quantity: this._redistributedCount(updatedPercentage),
        min: this._redistributedCount(updatedPercentage),
        max: this._redistributedCount(updatedPercentage),
        ...(groupCount && { percentage: updatedPercentage }),
      };
    }

    const updatedCount = this._updatedCount(quota, groupCount);
    return {
      ...quota.allocation,
      quantity: updatedCount,
      min: updatedCount,
      max: updatedCount,
      percentage: this._redistributedPercentage(updatedCount),
    };
  }

  private _updatedCount(quota: IndividualQuota, value: number): number {
    return Math.round(
      (quota.allocation.quantity / this._currentTotalAllocation()) * value
    );
  }

  private _updatedPercentage(quota: IndividualQuota, value: number): number {
    return Math.floor(
      (quota.allocation.percentage / this._currentTotalPercentage()) * value
    );
  }

  private _redistributedCount(quotaPercentage: number): number {
    return Math.round(quotaPercentage * this._surveyAllocation) / 100;
  }

  private _redistributedPercentage(quotaCount: number): number {
    return Math.floor((100 * (quotaCount || 0)) / this._surveyAllocation);
  }
}
